// City of Saratoga, CA — Residential Addition Permit Requirements
// Sources: saratoga.ca.us, Saratoga Municipal Code Ch. 15

export const zoningDistricts = [
  { district: "R-1 10,000", minLot: "10,000 sf", front: "25 ft", side: "10 / 15 ft", rear: "10 / 10 ft", coverage: "60%" },
  { district: "R-1 12,500", minLot: "12,500 sf", front: "25 ft", side: "10 / 15 ft", rear: "10 / 10 ft", coverage: "55%" },
  { district: "R-1 15,000", minLot: "15,000 sf", front: "25 ft", side: "12 / 17 ft", rear: "12 / 12 ft", coverage: "50%" },
  { district: "R-1 20,000", minLot: "20,000 sf", front: "25 ft", side: "Varies", rear: "Varies", coverage: "45%" },
  { district: "R-1 40,000", minLot: "40,000 sf", front: "30 ft", side: "20 / 25 ft", rear: "20 / 20 ft", coverage: "35%" },
  { district: "Hillside (HR)", minLot: "Varies", front: "30 ft", side: "20+ ft", rear: "20+ ft", coverage: "Varies" },
];

// How the City reviews the addition. Decides which Planning steps apply:
// Administrative Design Review adds story poles, the City's 250-ft notice
// and an appeal period; Ministerial review (§15-45.066) is approval against
// the objective design standards. Your planner confirms which applies.
export const reviewPaths = [
  { id: "unknown", label: "Not confirmed" },
  { id: "ministerial", label: "Administrative Ministerial" },
  { id: "administrative", label: "Administrative Design Review" },
];

// Who acts on a step. "city" steps are things you wait on, not do.
export const stepOwners = {
  you: { label: "You", cls: "bg-indigo-500/15 text-indigo-300" },
  architect: { label: "Architect", cls: "bg-purple-500/15 text-purple-300" },
  arborist: { label: "Arborist", cls: "bg-green-500/15 text-green-400" },
  contractor: { label: "Contractor", cls: "bg-amber-500/15 text-amber-400" },
  city: { label: "City", cls: "bg-gray-500/15 text-gray-400" },
};

const ADR_PROCESS_URL = "https://saratoga.ca.us/DocumentCenter/View/90/Administrative-Staff-Approval-PDF";
const APPLICATION_MATERIALS_URL = "https://www.saratoga.ca.us/168/Planning-Application-Materials";

// Step fields:
//   owner     who acts (see stepOwners)
//   parallel  can happen alongside the steps around it
//   onlyFor   review paths the step applies to (omitted = all)
//   optional  only if applicable to the project
//   source    city document the step is based on
// Ids are stable: checked state is stored by id (permits-checklist), so
// steps carried over from the old checklist keep their old ids.
export const permitPhases = [
  {
    id: "planning",
    name: "Planning Approval",
    accent: "blue",
    icon: "PenTool",
    items: [
      { id: "dr-7", text: "Submit Planning application in eTRAKiT", owner: "architect", note: "Development Application + Deposit Agreement + plans per the Design Review Submittal Checklist", source: APPLICATION_MATERIALS_URL },
      { id: "pl-complete", text: "Application deemed complete", owner: "city", note: "Staff has 30 days from each (re)submittal to check completeness and send comments", source: ADR_PROCESS_URL },
      { id: "pl-neighbors", text: "Neighbor Notification Form — signatures from touching neighbors + across the street, then upload to eTRAKiT", owner: "you", parallel: true, note: "The City lists this as a pre-submittal step; if you've already submitted, ask your planner about collecting it now while staff reviews. Neighbors can also mail it to the City directly.", source: APPLICATION_MATERIALS_URL },
      { id: "dr-4", text: "Arborist report for protected trees near the work", owner: "arborist", parallel: true, optional: true, note: "Protected: ≥10\" diameter (≥6\" for 9 native species). City Arborist: Christina Fusco, cfusco@saratoga.ca.us, 408-868-1276" },
      { id: "dr-9", text: "Respond to review comments (1–3 rounds typical)", owner: "architect" },
      { id: "pl-poles", text: "Install story poles; planner approves them", owner: "contractor", onlyFor: ["administrative"], note: "The notice won't go out until the poles are approved. They stay up until the appeal period ends.", source: ADR_PROCESS_URL },
      { id: "pl-notice", text: "City mails 250-ft Notice of Intent; 15-day comment period", owner: "city", onlyFor: ["administrative"], note: "Staff prepares and mails it — nothing for you to send", source: ADR_PROCESS_URL },
      { id: "dr-10", text: "Planning approval — Notice of Approval with conditions", owner: "city", note: "Ministerial: approved by the Community Development Director if the project meets every objective design standard (§15-45.066)" },
      { id: "pl-appeal", text: "15-day appeal period ends", owner: "city", onlyFor: ["administrative"], note: "Building plans can be submitted once no appeal is filed", source: ADR_PROCESS_URL },
    ],
  },
  {
    id: "building-permit",
    name: "Building Permit",
    accent: "amber",
    icon: "FileCheck",
    items: [
      { id: "bp-cd", text: "Construction drawings — structural, details, Title 24", owner: "architect", parallel: true, note: "Can start during Planning review; submitted after Planning approval" },
      { id: "bp-1", text: "Submit building permit in eTRAKiT", owner: "architect", note: "Include the Notice of Approval + conditions, arborist report (if any), Title 24, CALGreen checklist, smoke/CO self-certification, and the C&D Waste Management Plan (Green Halo)", source: ADR_PROCESS_URL },
      { id: "bp-plancheck", text: "Plan check corrections", owner: "architect" },
      { id: "bp-7", text: "Pay permit fees", owner: "you", note: "Based on project valuation — Building Division 408-868-1290" },
      { id: "bp-8", text: "Building permit issued", owner: "city", note: "Valid 12 months from activation; an inspection every 180 days keeps it active" },
    ],
  },
];

/** Steps that apply on this review path. Unconfirmed shows everything. */
export function stepsForPath(phase, reviewPath) {
  if (!reviewPath || reviewPath === "unknown") return phase.items;
  return phase.items.filter((i) => !i.onlyFor || i.onlyFor.includes(reviewPath));
}

export const keyContacts = [
  { dept: "Planning Division", phone: "408-868-1222", email: "planning@saratoga.ca.us" },
  { dept: "Building Division", phone: "408-868-1290", email: "" },
  { dept: "Building Inspections", phone: "408-868-1240", email: "" },
  { dept: "City Arborist", phone: "408-868-1276", email: "cfusco@saratoga.ca.us" },
];

export const keyFacts = [
  { label: "Permit Center", value: "13777 Fruitvale Ave, Saratoga, CA 95070" },
  { label: "Hours", value: "Mon-Fri 8AM-Noon, 1PM-5PM (closed Tue 11:30-Noon & alt Fridays)" },
  { label: "Online Portal", value: "eTRAKiT — sara.csqrcloud.com" },
  { label: "Permit Validity", value: "12 months from activation" },
  { label: "Inspections", value: "Schedule via eTRAKiT (8AM-12PM or 12PM-4PM) — tracked on the Timeline" },
  { label: "Inspection Interval", value: "Every 180 days to prevent expiration" },
  { label: "Waste Diversion", value: "65% minimum (C&D Waste Mgmt Plan required)" },
  { label: "FAR Formula", value: "0.40 - {(A - 2) / 3 × 0.05}" },
  { label: "Tree Protection", value: "≥10\" diameter (≥6\" for 9 native species)" },
];
